// What a page can be made of.
//
// Three of the archived project's nine element types survive. Two of them are
// the pair that matters: `section` POINTS AT a stored Section by id, so editing
// that section changes every page carrying it; `code` INLINES the same fields
// on the element itself, so it belongs to one page and nothing else can reach
// it. Reuse versus locality.
//
// `paragraph` is neither, and that is the point of it: prose written in a
// rich-text editor rather than in markup, so that the person adding a page of
// text does not have to open the HTML tab to write a bullet list. It stores a
// Quill delta and renders through `common/render/paragraph`.
//
// The three `blog-*` types are the blog (docs/pagews-blog.md): `blog-post`
// shows ONE published post, the one a `/blog/:slug` route names;
// `blog-list` the site's published posts a page at a time; and
// `blog-post-collection` a hand-ordered set of them. Each renders through a
// Section named in its config — a section is the template — or through a
// built-in default when none is named. See @common/render/blog.
//
// The rest of gen-2's list (capture, table-view, markdown) still needs an
// entity pagews does not have.
//
// The order of this array is load-bearing: `config` below resolves its branch by
// this enum's INDEX. See common/schema/types/params/utils.js for the mechanism.
// APPEND to it — inserting anywhere but the end re-points every element written
// after that position at the wrong branch of the oneOf.
export const types = [
  "section",
  "code",
  "paragraph",
  "blog-post",
  "blog-list",
  "blog-post-collection",
];

// Minted by the client (uuidv4) rather than the server, because an element has
// no round trip of its own — the whole array is written by the page mutation, so
// there is no moment at which a server could hand an id back.
export const elementid = {
  type: "string",
};

export const name = {
  $ref: "/Name",
  label: "Name",
  default: "",
};

export const type = {
  label: "Type",
  type: "string",
  default: "",
  enum: types,
  minLength: 1,
};

export const tags = {
  label: "Tags",
  type: "array",
  items: { type: "string" },
  widget: "tags",
  default: [],
};

// An EJS wrapper around whatever the element renders. `page:header` and
// `page:footer` in `tags` decide WHERE the element lands; `frame` decides what
// it is wrapped in when it gets there.
export const frame = {
  label: "Frame",
  type: "string",
  textarea: true,
  default: "<div><%-content%></div>",
};

// WHETHER THIS LANGUAGE'S COPY IS STILL THE SOURCE'S PROSE
//
// An element belongs to a translation group's structure and to one page's
// language at once (see ./translatable). When the group's structure moves, the
// element is propagated to every sibling carrying the source's prose, because
// there is nothing else to carry — and this is what says so, rather than
// leaving a Spanish page quietly showing English text with nothing on screen to
// find it by.
//
// SERVER-OWNED. api/code/src/model/page sets it when it propagates an element
// into a sibling and when `translate` creates a row, and clears it the moment
// an update's element holds different prose from the row's. It is on
// PageElementInput as well so the editor can clear it directly — the
// translation that is legitimately the same string as its source — but nothing
// on the wire can SET it, which is what stops a client that knows nothing about
// the field from leaving a page flagged forever.
//
// False for an element with nothing to translate: a `section` element points at
// a Section row every language already shares, and the site translation catalog
// is what renders it in each of them.
export const translationPending = {
  type: "boolean",
  default: false,
};

export const config = {
  label: "Configuration",
  type: "object",
  oneOf: [
    { $ref: "/Page/Element/Type/Section" },
    { $ref: "/Page/Element/Type/Code" },
    { $ref: "/Page/Element/Type/Paragraph" },
    { $ref: "/Page/Element/Type/BlogPost" },
    { $ref: "/Page/Element/Type/BlogList" },
    { $ref: "/Page/Element/Type/BlogPostCollection" },
  ],
  resolver: "type",
  default: {},
};
